import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Button, Badge, Modal, Form, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken, getUserId } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from '../contexts/LoadingContext';
import { useParams } from "react-router-dom";

const GRADE_THRESHOLDS = [
  { min: 80, grade: 4 },
  { min: 75, grade: 3.5 },
  { min: 70, grade: 3 },
  { min: 65, grade: 2.5 },
  { min: 60, grade: 2 },
  { min: 55, grade: 1.5 },
  { min: 50, grade: 1 },
  { min: 0,  grade: 0 },
];

const calculateGrade = (score) => {
  if (score === null || score === undefined || score === '') return null;
  const s = parseFloat(score);
  if (isNaN(s)) return null;
  for (const t of GRADE_THRESHOLDS) {
    if (s >= t.min) return t.grade;
  }
  return 0;
};

export default function ViewAdvisorGrade() {
  const { teach_id } = useParams();
  const { setLoading } = useLoading();
  const currentSkin = (localStorage.getItem('skin-mode')) ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    if (skin === 'dark') {
      const btnWhite = document.getElementsByClassName('btn-white');
      for (const btn of btnWhite) {
        btn.classList.add('btn-outline-primary');
        btn.classList.remove('btn-white');
      }
    } else {
      const btnOutlinePrimary = document.getElementsByClassName('btn-outline-primary');
      for (const btn of btnOutlinePrimary) {
        btn.classList.remove('btn-outline-primary');
        btn.classList.add('btn-white');
      }
    }
    const tables = document.getElementsByTagName('table');
    for (const table of tables) {
      const theads = table.querySelectorAll('thead');
      if (skin === 'dark') {
        for (const thead of theads) {
          thead.classList.remove('table-secondary');
          thead.classList.add('table-primary');
        }
        table.classList.add('table-dark');
      } else {
        table.classList.remove('table-dark');
        for (const thead of theads) {
          thead.classList.remove('table-primary');
          thead.classList.add('table-secondary');
        }
      }
    }
  };

  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const [data, setData] = useState([]);
  const [room, setRoom] = useState("");
  const [subject, setSubject] = useState("");
  const [subId, setSubId] = useState(null);
  const [yearDisplay, setYearDisplay] = useState("");

  useEffect(() => {
    const yearId = localStorage.getItem('yearId');
    if (!yearId) return;
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })
    .then(res => {
      const found = res.data.find(y => String(y.year_id) === String(yearId));
      if (found) setYearDisplay(`ปีการศึกษา ${found.year_year}`);
    })
    .catch(() => {});
  }, []);

  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [score, setScore] = useState('');
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudentsForGradeEntry/${teach_id}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })
    .then(response => {
      const records = response.data;
      if (records.length > 0) {
        const first = records[0];
        setRoom(first.tb_class_c_id + "/" + first.r_no);
        setSubject(first.sub_subid + " " + first.sub_subname);
        setSubId(first.sub_id);
        setData(records.map(r => ({
          study_id:    r.study_id,
          g_id:        r.g_id   ?? null,
          st_id:       r.st_id,
          st_idnumber: r.st_idnumber,
          st_name:     r.st_name,
          st_status:   r.st_status,
          g_grade:     r.g_grade  ?? null,
          g_waiting:   r.g_waiting ?? '0',
          update_by:   r.update_by ?? null,
          g_time:      r.g_time   ?? null,
        })));
      }
    })
    .catch(error => {
      Swal.fire({
        icon: 'error',
        title: error.response?.data?.title || 'เกิดข้อผิดพลาด',
        text: error.response?.data?.message || 'ไม่สามารถโหลดข้อมูลได้'
      });
    })
    .finally(() => setLoading(false));
  }, [teach_id, setLoading]);

  const handleEditClick = (student) => {
    setShowModal(true);
    setModalLoading(true);
    setModalData(null);

    axiosInstance.post(`${process.env.REACT_APP_API}/prepareGradeEdit`, {
      st_id:    student.st_id,
      teach_id,
      sub_id:   subId,
      study_id: student.study_id
    }, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })
    .then(res => {
      const d = res.data;
      setModalData(d);
      setScore(d.g_grade !== null && d.g_grade !== undefined ? String(d.g_grade) : '');
      setWaiting(d.g_waiting === '1');
    })
    .catch(err => {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: err.response?.data?.message || 'ไม่สามารถโหลดข้อมูลได้'
      });
      setShowModal(false);
    })
    .finally(() => setModalLoading(false));
  };

  const handleSave = () => {
    setModalLoading(true);
    axiosInstance.put(`${process.env.REACT_APP_API}/updateGrade`, {
      g_id:      modalData.g_id,
      g_grade:   score,
      g_waiting: waiting ? '1' : '0',
      update_by: String(getUserId())
    }, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })
    .then(() => {
      const newScore   = score === '' ? null : parseFloat(score);
      const newWaiting = waiting ? '1' : '0';
      setData(prev => prev.map(s =>
        s.study_id === modalData.study_id
          ? { ...s, g_grade: newScore, g_waiting: newWaiting, g_id: modalData.g_id }
          : s
      ));
      setShowModal(false);
      Swal.fire({ title: 'บันทึกสำเร็จ', icon: 'success', timer: 1200, showConfirmButton: false });
    })
    .catch(err => {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: err.response?.data?.message || 'ไม่สามารถบันทึกได้'
      });
    })
    .finally(() => setModalLoading(false));
  };

  const previewGrade = calculateGrade(score);

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ที่ปรึกษาห้องเรียน</Link></li>
              <li className="breadcrumb-item"><Link to="/advisor/grade">ผลการเรียนรายวิชา</Link></li>
              <li className="breadcrumb-item active" aria-current="page">ม.{room}</li>
            </ol>
            <h4 className="main-title mb-0">
              วิชา: <u>{subject}</u>&ensp;ชั้น <u>ม.{room}</u>
            </h4>
          </div>
          {yearDisplay && (
            <div className="text-end mt-2 mt-sm-0">
              <span className="badge bg-primary fs-6 fw-normal">{yearDisplay}</span>
            </div>
          )}
        </div>

        <Row className="g-12 mb-4">
          <Col xl="12">
            <Card className="card-one">
              <Card.Body className="p-3">
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th className="text-center" style={{ width: "50px" }}>เลขที่</th>
                      <th className="text-center" style={{ width: "110px" }}>เลขประจำตัว</th>
                      <th>ชื่อ-สกุล</th>
                      <th className="text-center" style={{ width: "80px" }}>คะแนน</th>
                      <th className="text-center" style={{ width: "100px" }}>ผลการเรียน</th>
                      <th className="text-center" style={{ width: "90px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length > 0 ? data.map((student, index) => {
                      const grade = calculateGrade(student.g_grade);
                      return (
                        <tr key={student.study_id} className={student.st_status === '0' ? 'table-danger' : ''}>
                          <th scope="row" className="text-center">{index + 1}</th>
                          <td className="text-center">{student.st_idnumber}</td>
                          <td>{student.st_name}</td>
                          <td className="text-center" style={{ color: 'blue' }}>
                            {student.g_grade !== null ? student.g_grade : '-'}
                          </td>
                          <td className="text-center">
                            {student.g_id === null
                              ? '-'
                              : student.g_waiting === '1'
                                ? <span style={{ color: 'red', fontWeight: 'bold' }}>ร</span>
                                : grade !== null
                                  ? <Badge bg={grade >= 2 ? 'success' : grade >= 1 ? 'warning' : 'danger'}>{grade}</Badge>
                                  : '-'
                            }
                          </td>
                          <td className="text-center">
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0"
                              onClick={() => handleEditClick(student)}
                              title={student.update_by ? `อัปเดตโดย: ${student.update_by}` : ''}
                            >
                              <i className="ri-edit-2-fill"></i> แก้ไข
                            </Button>
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr><td colSpan={6} className="text-center">ไม่พบข้อมูล</td></tr>
                    )}
                  </tbody>
                </Table>
                <div className="mt-3 d-flex justify-content-end">
                  <Link to="/advisor/grade" className="btn btn-secondary btn-sm">
                    <i className="ri-arrow-left-line"></i> ย้อนกลับ
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Modal show={showModal} onHide={() => setShowModal(false)} size="sm" centered>
          <Modal.Header closeButton>
            <Modal.Title as="h6">
              <i className="ri-edit-2-fill"></i> เพิ่มคะแนน / อัปเดตคะแนน
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalLoading && !modalData ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" /> กำลังโหลด...
              </div>
            ) : modalData && (
              <Form>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 fw-semibold">เลขประจำตัว</Form.Label>
                  <Form.Control size="sm" value={modalData.st_idnumber || ''} disabled />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 fw-semibold">ชื่อ-สกุล</Form.Label>
                  <Form.Control size="sm" value={modalData.st_name || ''} disabled />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 fw-semibold">วิชา</Form.Label>
                  <Form.Control size="sm" value={`${modalData.sub_subid || ''} ${modalData.sub_subname || ''}`} disabled />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 fw-semibold">
                    คะแนน
                    {previewGrade !== null && score !== '' && (
                      <span className="ms-2 text-muted fw-normal">
                        → ผลการเรียน: <strong style={{ color: 'blue' }}>{previewGrade}</strong>
                      </span>
                    )}
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={score}
                    onChange={e => setScore(e.target.value)}
                    placeholder="กรอกคะแนนที่ต้องการ"
                    autoFocus
                  />
                </Form.Group>
                <Form.Check
                  type="checkbox"
                  id="waiting-check-advisor"
                  label={<>ติดค้าง กรณีที่นักเรียนติด <span style={{ color: 'red', fontWeight: 'bold' }}>ร</span></>}
                  checked={waiting}
                  onChange={e => setWaiting(e.target.checked)}
                />
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer className="py-2">
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>
              <i className="ri-close-line"></i> ปิด
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={handleSave}
              disabled={modalLoading || !modalData}
            >
              {modalLoading ? <Spinner animation="border" size="sm" /> : <><i className="ri-check-line"></i> บันทึก</>}
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}
